import React, { useState } from 'react';
import {
  Avatar,
  AvatarGroup,
  Box,
  Tooltip,
  Typography,
  useTheme,
  Popover,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Divider,
} from '@mui/material';
import { Person, Email, Phone, Chat } from '@mui/icons-material';
import { useContactDrawer } from '../Contacts/ContactDrawer';
import ClickableContactName from '../common/ClickableContactName';

interface ParticipatingEntity {
  id?: string;
  role: string;
  name?: string;
  email?: string;
  phone?: string;
  contact_id?: string;
  identifier?: string;
  tag?: string;
  valid_email_user?: boolean;
  valid_phone_user?: boolean;
}

interface ConversationParticipantsProps {
  participatingEntities?: ParticipatingEntity[];
  maxAvatars?: number;
  avatarSize?: 'small' | 'medium' | 'large';
  showOthersCount?: boolean;
  onClick?: (entity: ParticipatingEntity) => void;
}

const ConversationParticipants: React.FC<ConversationParticipantsProps> = ({
  participatingEntities = [],
  maxAvatars = 2,
  avatarSize = 'medium',
  showOthersCount = true,
  onClick,
}) => {
  const theme = useTheme();
  const { openDrawer } = useContactDrawer();
  const isDarkMode = theme.palette.mode === 'dark';
  
  // Popover state for showing additional participants
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const popoverOpen = Boolean(anchorEl);

  // Filter out non-contact entities
  const validEntities = participatingEntities.filter(
    e => e.role !== 'assistant' && e.role !== 'function' && (e.name || e.email || e.phone)
  );



  // Avatar sizes
  const avatarSizes = {
    small: { width: 32, height: 32, fontSize: '0.8rem' },
    medium: { width: 36, height: 36, fontSize: '0.85rem' },
    large: { width: 40, height: 40, fontSize: '0.9rem' },
  };

  const currentSize = avatarSizes[avatarSize];

  // Helper function to generate deterministic color based on entity identifier
  const getEntityColor = (entity: ParticipatingEntity) => {
    const identifier = entity.name || entity.email || entity.phone || entity.identifier || '';
    const hash = identifier.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const hue = hash % 360;
    const saturation = 25 + (hash % 20); // 25-45% saturation for muted colors
    const lightness = isDarkMode ? 35 + (hash % 15) : 55 + (hash % 15);
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  // Helper function to get initials
  const getInitials = (entity: ParticipatingEntity) => {
    if (entity.name) {
      const parts = entity.name.split(' ').filter(p => p.length > 0);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return entity.name[0].toUpperCase();
    }
    if (entity.email) {
      // Try to extract meaningful initials from email
      const emailParts = entity.email.split('@')[0];
      const nameParts = emailParts.split(/[._-]/).filter(p => p.length > 0);
      if (nameParts.length >= 2) {
        return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
      }
      return emailParts.slice(0, 2).toUpperCase();
    }
    if (entity.phone || entity.identifier) {
      return 'P'; // Use 'P' for phone-only contacts
    }
    return '';
  };

  // Handle click on avatar
  const handleAvatarClick = (entity: ParticipatingEntity, event: React.MouseEvent) => {
    event.stopPropagation();
    
    // First try custom onClick handler
    if (onClick) {
      onClick(entity);
      return;
    }
    
    // Default behavior - open contact drawer if user entity
    if (entity.role === 'user' && entity.id) {
      openDrawer(entity.id);
    }
  };

  // If no valid entities, show placeholder
  if (validEntities.length === 0) {
    return (
      <Avatar
        sx={{
          ...currentSize,
          bgcolor: theme.palette.grey[400],
          color: theme.palette.grey[700],
          border: `2px solid ${theme.palette.background.paper}`,
          cursor: 'pointer',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Person />
      </Avatar>
    );
  }

  // Render tooltip content for an entity
  const renderEntityTooltip = (entity: ParticipatingEntity) => (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
        {entity.name || entity.email || entity.identifier || 'Unknown Contact'}
      </Typography>
      {(() => {
        // Prioritize the valid_email_user and valid_phone_user flags
        const isEmailUser = entity.valid_email_user === true || (!entity.valid_phone_user && entity.email && entity.email.trim() !== '');
        const isPhoneUser = entity.valid_phone_user === true || (!entity.valid_email_user && (entity.phone && entity.phone.trim() !== '' || entity.identifier && entity.identifier.startsWith('+')));
        const isChat = !isEmailUser && !isPhoneUser && entity.name;

        if (isChat) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Chat sx={{ fontSize: 10, verticalAlign: 'middle', mr: 0.5 }} />
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, fontSize: '0.65rem' }}>
                Chat
              </Typography>
            </Box>
          );
        }

        return (
          <>
            {isEmailUser && (
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, fontSize: '0.65rem' }}>
                <Email sx={{ fontSize: 10, verticalAlign: 'middle', mr: 0.5 }} />
                {entity.email || (entity.identifier && entity.identifier.includes('@') ? entity.identifier : '')}
              </Typography>
            )}
            {isPhoneUser && !isEmailUser && (
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.8, fontSize: '0.65rem' }}>
                <Phone sx={{ fontSize: 10, verticalAlign: 'middle', mr: 0.5 }} />
                {entity.phone || entity.identifier}
              </Typography>
            )}
          </>
        );
      })()}
      {entity.role === 'user' && entity.id && (
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            opacity: 0.6, 
            fontSize: '0.6rem',
            mt: 0.5,
            fontStyle: 'italic'
          }}
        >
          Click to view contact
        </Typography>
      )}
    </Box>
  );

  // Handle click on "others" count
  const handleOthersClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget as HTMLElement);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {/* Only show AvatarGroup if maxAvatars > 0 */}
      {maxAvatars > 0 && (
        <AvatarGroup
          sx={{
            '& .MuiAvatar-root': {
              ...currentSize,
              border: `2px solid ${theme.palette.background.paper}`,
              transition: 'all 0.2s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'scale(1.05)',
                zIndex: 2,
              },
            },
            '& .MuiAvatarGroup-avatar': {
              ...currentSize,
              cursor: 'default',
              '&:hover': {
                transform: 'none',
              },
            },
          }}
        >
          {validEntities.slice(0, maxAvatars).map((entity, idx) => (
            <Tooltip
              key={idx}
              title={renderEntityTooltip(entity)}
              arrow
              placement="bottom"
            >
              <Avatar
                onClick={(e) => handleAvatarClick(entity, e)}
                sx={{
                  bgcolor: getEntityColor(entity),
                  color: isDarkMode ? 'grey.100' : 'grey.800',
                  fontWeight: 600,
                }}
              >
                {getInitials(entity)}
              </Avatar>
            </Tooltip>
          ))}
        </AvatarGroup>
      )}

      {/* Show "+X others" count if there are more participants than shown */}
      {showOthersCount && validEntities.length - maxAvatars - 1 > 0 && (
        <Typography
          component="span"
          variant="caption"
          sx={{
            color: 'text.secondary',
            fontWeight: 500,
            fontSize: avatarSize === 'small' ? '0.7rem' : '0.75rem',
            cursor: 'pointer',
            textDecoration: 'underline',
            textDecorationStyle: 'dotted',
            textUnderlineOffset: '2px',
            '&:hover': {
              color: theme.palette.primary.main,
            },
          }}
          onClick={handleOthersClick}
        >
          +{validEntities.length - maxAvatars - 1} {validEntities.length - maxAvatars - 1 === 1 ? 'other' : 'others'}
        </Typography>
      )}

      {/* Popover for additional participants */}
      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 0.5,
            boxShadow: theme.shadows[8],
            borderRadius: 2,
            minWidth: 200,
            maxWidth: 300,
          }
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 1, fontWeight: 600 }}>
            All Participants ({validEntities.length})
          </Typography>
          <Divider />
          <List sx={{ py: 1 }}>
            {validEntities.map((entity, idx) => (
            <ListItem key={idx} disablePadding>
              <ListItemButton
                onClick={(e) => {
                  setAnchorEl(null);
                  handleAvatarClick(entity, e);
                }}
                sx={{
                  py: 1,
                  px: 2,
                  '&:hover': {
                    bgcolor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                  }
                }}
              >
                <ListItemAvatar sx={{ minWidth: 40 }}>
                  <Avatar
                    sx={{
                      bgcolor: getEntityColor(entity),
                      color: isDarkMode ? 'grey.100' : 'grey.800',
                      width: 32,
                      height: 32,
                      fontSize: '0.8rem',
                      fontWeight: 600,
                    }}
                  >
                    {getInitials(entity)}
                  </Avatar>
                </ListItemAvatar>
                                 <ListItemText
                   primary={
                     <ClickableContactName
                       contactId={entity.role === 'user' ? entity.id : undefined}
                       contactName={entity.name || entity.email || entity.identifier || 'Unknown Contact'}
                       variant="body2"
                       sx={{ fontWeight: 500, fontSize: '0.875rem' }}
                       onClick={() => {
                         setAnchorEl(null);
                         if (entity.role === 'user' && entity.id) {
                           openDrawer(entity.id);
                         }
                       }}
                     />
                   }
                   secondary={
                     <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                       {(() => {
                         // Prioritize the valid_email_user and valid_phone_user flags
                         const isEmailUser = entity.valid_email_user === true || (!entity.valid_phone_user && entity.email && entity.email.trim() !== '');
                         const isPhoneUser = entity.valid_phone_user === true || (!entity.valid_email_user && (entity.phone && entity.phone.trim() !== '' || entity.identifier && entity.identifier.startsWith('+')));
                         const isChat = !isEmailUser && !isPhoneUser && entity.name;

                         if (isChat) {
                           return (
                             <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                               <Chat sx={{ fontSize: 12, opacity: 0.7 }} />
                               <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.75rem' }}>
                                 Chat
                               </Typography>
                             </Box>
                           );
                         }

                         return (
                           <>
                             {isEmailUser && (
                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                 <Email sx={{ fontSize: 12 }} />
                                 <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                   {entity.email || (entity.identifier && entity.identifier.includes('@') ? entity.identifier : '')}
                                 </Typography>
                               </Box>
                             )}
                             {isPhoneUser && !isEmailUser && (
                               <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                                 <Phone sx={{ fontSize: 12 }} />
                                 <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                   {entity.phone || entity.identifier}
                                 </Typography>
                               </Box>
                             )}
                           </>
                         );
                       })()}
                     </Box>
                   }
                 />
              </ListItemButton>
            </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    </Box>
  );
};

export default ConversationParticipants; 
